import React, { Fragment, memo, type CSSProperties, type ReactNode } from 'react';
import { rowHitShell } from './row-hit-shell';
import {
  BlockRenderer,
  type RenderMergeMode,
} from './BlockRenderer';
import {
  padStyle,
  presentationTableProps,
  rowEdgeColumnRadiusStyle,
  surfaceBorderTableStyle,
  surfaceCellBorderStyle,
  surfaceCellClassName,
  surfaceContentClipStyle,
} from './table';
import {
  columnHasStackDividers,
  columnResponsiveClasses,
 EMAIL_COL_GAP_CLASS } from '../domain/presentation';
import type { FacilityProfile } from '../domain/facility';
import {
  emailRowAreaWidth,
  resolveColumnPixelWidths,
  rowInnerWidth,
} from '../domain/column-presets';
import { ZERO_PADDING, ZERO_RADIUS } from '../domain/create-shared';
import {
  surfaceBackgroundImageStyle,
} from '../domain/image-overlay';
import { rowHasContentGutter } from '../domain/factories';
import {
  blockContentMaxWidth,
} from '../domain/block-width';
import { columnHasFillColumnImage } from '../domain/image-height';
import type {
  Block,
  Column,
  Group,
  Padding,
  Row,
} from '../domain/types';
import type { RenderTheme } from '../domain/text-theme';

export type RowChrome = {
  wrapGroup?: (group: Group, node: ReactNode) => ReactNode;
  wrapEmptyGroupRows?: (group: Group) => ReactNode;
  wrapGroupedRow?: (
    group: Group,
    row: Row,
    rowIndex: number,
    node: ReactNode,
  ) => ReactNode;
  wrapRow?: (row: Row, node: ReactNode) => ReactNode;
  wrapColumn?: (
    row: Row,
    column: Column,
    node: ReactNode,
    padding: Padding,
  ) => ReactNode;
  wrapColumnRow?: (row: Row, node: ReactNode) => ReactNode;
  wrapBlock?: (
    row: Row,
    column: Column,
    block: Block,
    node: ReactNode,
  ) => ReactNode;
};

function columnShellStyle(
  column: Column,
  width: number,
  editorMode: boolean,
): CSSProperties {
  const radius = column.borderRadius ?? ZERO_RADIUS;
  return {
    width,
    verticalAlign: column.verticalAlign,
    backgroundColor:
      column.backgroundColor === 'transparent'
        ? undefined
        : column.backgroundColor,
    ...surfaceBackgroundImageStyle(
      column.backgroundImage || undefined,
      column.backgroundImageOverlay,
    ),
    ...surfaceCellBorderStyle(column.border, radius, editorMode),
  };
}

function columnOuterCellStyle(
  column: Column,
  width: number,
  editorMode: boolean,
): CSSProperties {
  return {
    ...columnShellStyle(column, width, editorMode),
    boxSizing: 'border-box',
  };
}

type RowRendererProps = {
  row: Row;
  contentWidth: number;
  pagePadding?: Padding;
  facility: FacilityProfile;
  textTheme: RenderTheme;
  mergeMode: RenderMergeMode;
  chrome?: RowChrome;
  editorMode?: boolean;
  ghost?: boolean;
  nested?: boolean;
  group?: Group;
  groupRowIndex?: number;
};

export const RowRenderer = memo(({
  row,
  contentWidth,
  pagePadding = ZERO_PADDING,
  facility,
  textTheme,
  mergeMode,
  chrome,
  editorMode = false,
  ghost = false,
  nested = false,
  group,
  groupRowIndex = 0,
}: RowRendererProps) => {
  const useRowHitShell = Boolean(chrome?.wrapRow);
  const rowContentWidth = useRowHitShell
    ? emailRowAreaWidth(contentWidth, pagePadding)
    : contentWidth;
  const innerWidth = rowInnerWidth(rowContentWidth, row.padding, row.border);
  const widths = resolveColumnPixelWidths(
    row.columnWidths,
    innerWidth,
    row.columnGap,
  );

  const columnsTable = (
    <table
      {...presentationTableProps()}
      width="100%"
      style={{
        ...presentationTableProps().style,
        width: '100%',
        tableLayout: 'fixed',
      }}>
      <tbody>
        <tr>
          {row.columns.map((column, index) => (
            <ColumnCells
              key={column.id}
              row={row}
              column={column}
              columnIndex={index}
              width={widths[index] ?? 0}
              gap={index < row.columns.length - 1 ? row.columnGap : 0}
              facility={facility}
              textTheme={textTheme}
              mergeMode={mergeMode}
              chrome={chrome}
              editorMode={editorMode}
            />
          ))}
        </tr>
      </tbody>
    </table>
  );
  const wrappedColumnsTable = chrome?.wrapColumnRow
    ? chrome.wrapColumnRow(row, columnsTable)
    : columnsTable;

  const rowRadius = row.borderRadius ?? ZERO_RADIUS;
  const rowBorderTableStyle = surfaceBorderTableStyle(row.border, rowRadius);
  const rowContentCellStyle: CSSProperties = {
    ...padStyle(row.padding),
    backgroundColor:
      row.backgroundColor === 'transparent'
        ? undefined
        : row.backgroundColor,
    ...surfaceBackgroundImageStyle(
      row.backgroundImage || undefined,
      row.backgroundImageOverlay,
    ),
    ...surfaceContentClipStyle(rowRadius, editorMode),
  };

  const innerTable = (
    <table
      {...presentationTableProps()}
      className={[
        'email-inner',
        editorMode ? 'email-rail-anchor' : '',
        rowHasContentGutter(row) ? 'row-content-gutter' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      width="100%"
      style={{
        ...presentationTableProps().style,
        width: editorMode ? rowContentWidth : '100%',
        maxWidth: rowContentWidth,
        ...(row.responsive && row.columns.length > 1 && row.columnGap > 0
          ? { ['--row-col-gap' as string]: `${row.columnGap}px` }
          : {}),
      }}>
      <tbody>
        <tr>
          <td style={{ padding: 0, verticalAlign: 'top' }}>
            <table
              {...presentationTableProps()}
              width="100%"
              style={{
                ...presentationTableProps().style,
                ...rowBorderTableStyle,
              }}>
              <tbody>
                <tr>
                  <td style={rowContentCellStyle}>{wrappedColumnsTable}</td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );

  if (ghost) {
    return innerTable;
  }

  if (nested) {
    if (chrome?.wrapGroupedRow && group) {
      return (
        <>
          {chrome.wrapGroupedRow(
            group,
            row,
            groupRowIndex,
            rowHitShell(innerTable),
          )}
        </>
      );
    }
    return innerTable;
  }

  if (!chrome?.wrapRow) {
    return innerTable;
  }

  return <>{chrome.wrapRow(row, rowHitShell(innerTable))}</>;
});
RowRenderer.displayName = 'RowRenderer';

function ColumnCells({
  row,
  column,
  columnIndex,
  width,
  gap,
  facility,
  textTheme,
  mergeMode,
  chrome,
  editorMode,
}: {
  row: Row;
  column: Column;
  columnIndex: number;
  width: number;
  gap: number;
  facility: FacilityProfile;
  textTheme: RenderTheme;
  mergeMode: RenderMergeMode;
  chrome?: RowChrome;
  editorMode: boolean;
}) {
  const blocks = column.blocks.map(block => {
    const columnContentWidth = Math.max(
      0,
      width - column.padding.left - column.padding.right,
    );
    const blockPadding = "padding" in block ? block.padding : ZERO_PADDING;
    const contentMaxWidth = blockContentMaxWidth(
      columnContentWidth,
      blockPadding,
    );
    const rendered = (
      <BlockRenderer
        block={block}
        facility={facility}
        textTheme={textTheme}
        mergeMode={mergeMode}
        editorMode={editorMode}
        contentMaxWidth={contentMaxWidth}
      />
    );
    if (!chrome?.wrapBlock) {
      return <Fragment key={block.id}>{rendered}</Fragment>;
    }
    return (
      <Fragment key={block.id}>
        {chrome.wrapBlock(row, column, block, rendered)}
      </Fragment>
    );
  });

  const inner = chrome?.wrapColumn
    ? chrome.wrapColumn(row, column, <>{blocks}</>, column.padding)
    : blocks;
  const equalHeightColumns = row.columns.length > 1;
  const fillColumnStretch = columnHasFillColumnImage(row, column);
  const emptyColumnStretch =
    editorMode && equalHeightColumns && column.blocks.length === 0;
  const stretchColumnHeight = fillColumnStretch || emptyColumnStretch;
  const nestedTableStyle: CSSProperties = {
    ...presentationTableProps().style,
    width: '100%',
    ...(stretchColumnHeight ? { height: '100%' } : {}),
  };
  const nestedCellStyle: CSSProperties = {
    ...(chrome?.wrapColumn ? undefined : padStyle(column.padding)),
    ...(stretchColumnHeight ? { height: '100%' } : {}),
  };
  const equalHeightStyle: CSSProperties | undefined = equalHeightColumns
    ? { height: 1 }
    : undefined;
  const baseTdStyle = {
    ...columnOuterCellStyle(column, width, editorMode),
    ...equalHeightStyle,
    verticalAlign: column.verticalAlign,
  };
  const stackDividerRole =
    row.responsive &&
    columnHasStackDividers(column, columnIndex, row.columns.length);
  const columnRadius = column.borderRadius ?? ZERO_RADIUS;
  const rowRadius = row.borderRadius ?? ZERO_RADIUS;
  const responsiveClassName = row.responsive
    ? columnResponsiveClasses(column, columnIndex, row.columns.length).join(' ')
    : undefined;
  const surfaceClassName = surfaceCellClassName(columnRadius, editorMode);
  const columnClassName = [responsiveClassName, surfaceClassName]
    .filter(Boolean)
    .join(' ') || undefined;
  const tdStyle: CSSProperties = {
    ...(stackDividerRole
      ? { ...baseTdStyle, ['--col-divider-color' as string]: column.border.color }
      : baseTdStyle),
    ...rowEdgeColumnRadiusStyle(
      rowRadius,
      columnRadius,
      columnIndex,
      row.columns.length,
      editorMode,
    ),
  };

  return (
    <>
      <td
        className={columnClassName}
        width={width}
        valign={column.verticalAlign}
        style={tdStyle}>
        <table
          {...presentationTableProps()}
          width="100%"
          style={nestedTableStyle}>
          <tbody style={stretchColumnHeight ? { height: '100%' } : undefined}>
            <tr style={stretchColumnHeight ? { height: '100%' } : undefined}>
              <td style={nestedCellStyle}>{inner}</td>
            </tr>
          </tbody>
        </table>
      </td>
      {gap > 0 ? (
        <td
          className={row.responsive ? EMAIL_COL_GAP_CLASS : undefined}
          width={gap}
          style={{
            width: gap,
            fontSize: 0,
            lineHeight: 0,
          }}>
          {'\u00a0'}
        </td>
      ) : null}
    </>
  );
}
